// Campos de destinatarios del modal de correo: chips removibles (PARA/CC) con el
// selector "+ Agregar contacto". El selector busca sobre contactos UNIFICADOS
// (proveedores + contactos del cliente del caso + asesores) con filtro incremental
// por coincidencia, y mantiene el "email libre" como fallback.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  type ContactoUnificado,
  type FuenteContacto,
  rankContactos,
} from "./contactosUnificados";

export type Destinatario = { nombre: string; email: string };

export function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs uppercase tracking-wide text-zinc-400">
      {children}
    </span>
  );
}

// Insignia de origen del contacto (proveedor / cliente / asesor / interno).
const FUENTE_META: Record<FuenteContacto, { label: string; clase: string }> = {
  proveedor: { label: "Proveedor", clase: "bg-accent-500/20 text-accent-300" },
  cliente: { label: "Cliente", clase: "bg-sky-500/20 text-sky-300" },
  asesor: { label: "Asesor", clase: "bg-teal-500/20 text-teal-300" },
  interno: { label: "Interno", clase: "bg-amber-500/20 text-amber-300" },
};

// ============================================================================
// ChipsField — input visual para destinatarios TO/CC con chips removibles +
// selector "+ Agregar contacto". Reusado para PARA y CC del modal.
// ============================================================================

export function ChipsField({
  label,
  destinatarios,
  onRemove,
  emptyHint,
  selectorOpen,
  onSelectorOpenChange,
  contactos,
  onPick,
  agregarEmail,
  agregarNombre,
  setAgregarEmail,
  setAgregarNombre,
  onAgregarLibre,
}: {
  label: string;
  destinatarios: Destinatario[];
  onRemove: (idx: number) => void;
  emptyHint: string;
  selectorOpen: boolean;
  onSelectorOpenChange: (o: boolean) => void;
  contactos: ContactoUnificado[];
  onPick: (nombre: string, email: string) => void;
  agregarEmail: string;
  agregarNombre: string;
  setAgregarEmail: (s: string) => void;
  setAgregarNombre: (s: string) => void;
  onAgregarLibre: () => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 space-y-2">
        <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
          {destinatarios.length === 0 ? (
            <p className="text-xs text-zinc-500 italic">{emptyHint}</p>
          ) : (
            destinatarios.map((d, idx) => (
              <span
                key={`${d.email}-${idx}`}
                className="inline-flex max-w-full items-center gap-1 text-xs px-2 py-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-200"
              >
                <span className="truncate min-w-0">{d.nombre}</span>
                <span className="truncate min-w-0 text-zinc-400">{`<${d.email}>`}</span>
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="hover:text-red-300 ml-1"
                  aria-label={`Quitar a ${d.nombre} (${d.email})`}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
        <AgregarContactoSelector
          open={selectorOpen}
          onOpenChange={onSelectorOpenChange}
          contactos={contactos}
          onPick={onPick}
          agregarEmail={agregarEmail}
          agregarNombre={agregarNombre}
          setAgregarEmail={setAgregarEmail}
          setAgregarNombre={setAgregarNombre}
          onAgregarLibre={onAgregarLibre}
        />
      </div>
    </div>
  );
}

// ============================================================================
// AgregarContactoSelector — picker para "+ Agregar contacto": busca sobre los
// contactos guardados (proveedores + cliente del caso + asesores) y ofrece
// "email libre" como fallback.
// ============================================================================

function AgregarContactoSelector({
  open,
  onOpenChange,
  contactos,
  onPick,
  agregarEmail,
  agregarNombre,
  setAgregarEmail,
  setAgregarNombre,
  onAgregarLibre,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  contactos: ContactoUnificado[];
  onPick: (nombre: string, email: string) => void;
  agregarEmail: string;
  agregarNombre: string;
  setAgregarEmail: (s: string) => void;
  setAgregarNombre: (s: string) => void;
  onAgregarLibre: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const buscarRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");

  const filtrados = useMemo(
    () => rankContactos(query, contactos).slice(0, 40),
    [query, contactos],
  );

  // ESC y click-outside para cerrar el selector cuando esta abierto.
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onOpenChange(false);
      }
    }
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onOpenChange(false);
      }
    }
    document.addEventListener("keydown", handleKey);
    // Pequeño defer para no captar el mismo click que abrió el selector
    const id = setTimeout(() => document.addEventListener("mousedown", handleClick), 0);
    // Foco al buscador al abrir
    buscarRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
      clearTimeout(id);
    };
  }, [open, onOpenChange]);

  // Al cerrar, limpiar la búsqueda para la próxima apertura.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        className="btn-ghost text-xs text-accent-400 hover:text-accent-300"
      >
        + Agregar contacto
      </button>
    );
  }

  return (
    <div
      ref={containerRef}
      className="rounded-lg border border-ink-700 bg-ink-900 shadow-xl p-3 space-y-3"
    >
      <div className="flex items-center justify-between border-b border-ink-700 pb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-zinc-200">
            Agregar destinatario
          </span>
          <span className="text-[10px] text-zinc-600">(ESC para cerrar)</span>
        </div>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Cerrar"
          className="h-6 w-6 rounded flex items-center justify-center text-zinc-400 hover:text-white hover:bg-ink-700 text-lg leading-none"
        >
          ×
        </button>
      </div>

      <div className="space-y-1.5">
        <input
          ref={buscarRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Busca por nombre, correo o empresa…"
          className="input text-xs w-full"
        />
        {contactos.length > 0 && (
          <div className="max-h-48 overflow-y-auto rounded border border-ink-700">
            {filtrados.length === 0 ? (
              <p className="p-2 text-xs text-zinc-500 italic">
                Sin coincidencias entre tus contactos guardados. Usa el email libre
                de abajo.
              </p>
            ) : (
              <ul className="divide-y divide-ink-800">
                {filtrados.map((c) => (
                  <li key={c.key}>
                    <button
                      type="button"
                      onClick={() => onPick(c.nombre, c.email)}
                      className="w-full text-left p-2 hover:bg-ink-800 text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-200 truncate">{c.nombre}</span>
                        <span
                          className={`text-[9px] uppercase px-1 rounded ${FUENTE_META[c.fuente].clase}`}
                        >
                          {FUENTE_META[c.fuente].label}
                        </span>
                        {c.esPrincipal && (
                          <span className="text-[9px] uppercase px-1 rounded bg-zinc-500/20 text-zinc-300">
                            principal
                          </span>
                        )}
                      </div>
                      <div className="text-zinc-500 truncate">
                        {c.email} · <span className="text-zinc-600">{c.etiqueta}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="space-y-1.5 pt-2 border-t border-ink-700">
        <p className="text-xs uppercase tracking-wide text-zinc-500">
          O escribe un email libre
        </p>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            value={agregarNombre}
            onChange={(e) => setAgregarNombre(e.target.value)}
            placeholder="Nombre"
            className="input text-xs"
          />
          <input
            type="email"
            value={agregarEmail}
            onChange={(e) => setAgregarEmail(e.target.value)}
            placeholder="email@x.com"
            className="input text-xs"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onAgregarLibre();
              }
            }}
          />
        </div>
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={onAgregarLibre}
            disabled={!agregarEmail.trim()}
            className="btn-primary text-xs disabled:opacity-50"
          >
            + Agregar
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-ghost text-xs"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
